/**
 * Solana Staking Decoding SDK
 *
 * Stateless functions for decoding, summarizing, and validating Solana
 * Stake Program transactions.
 *
 * @example
 * ```ts
 * import { solana } from "@blockdaemon/transaction-verifier";
 *
 * // Summarize a transaction
 * const summary = solana.summarizeUnsignedTransaction(base64Tx);
 *
 * // Validate with rules
 * const { ok, errors } = solana.validateUnsignedTransaction(base64Tx, {
 *   requireDelegate: true,
 *   forbidAuthorize: true,
 * });
 * ```
 *
 * @module
 */
import { decodeUnsignedTransactionB64, summarizeTransaction, TxAssertionBuilder, } from "./tx-utils.js";
function normalizeUnsignedInput(b64OrHex) {
    const hexLike = /^(0x)?[0-9a-fA-F]+$/.test(b64OrHex);
    if (!hexLike)
        return b64OrHex;
    const hex = b64OrHex.startsWith("0x") ? b64OrHex.slice(2) : b64OrHex;
    const out = new Uint8Array(hex.length / 2);
    for (let i = 0; i < out.length; i++)
        out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    const maybeBuffer = globalThis.Buffer;
    if (maybeBuffer?.from)
        return maybeBuffer.from(out).toString("base64");
    let s = "";
    for (let i = 0; i < out.length; i++)
        s += String.fromCharCode(out[i]);
    const maybeBtoa = globalThis.btoa;
    if (typeof maybeBtoa === "function")
        return maybeBtoa(s);
    throw new Error("No base64 encoder available");
}
/**
 * Decodes an unsigned Solana transaction from base64 or hex string.
 *
 * @param b64OrHex - The unsigned transaction as base64 (preferred) or hex string
 * @returns The decoded transaction object
 */
export function decodeUnsignedTransaction(b64OrHex) {
    const b64 = normalizeUnsignedInput(b64OrHex);
    return decodeUnsignedTransactionB64(b64);
}
/**
 * Returns a human-readable summary of an unsigned Solana transaction.
 *
 * The summary includes fee payer, accounts, and decoded instructions with
 * program-specific metadata (Stake/System program details).
 *
 * @param b64OrHex - The unsigned transaction as base64 (preferred) or hex string
 * @returns Transaction summary with instructions, accounts, and metadata
 *
 * @example
 * ```ts
 * const summary = solana.summarizeUnsignedTransaction(base64Tx);
 * console.log("Fee payer:", summary.feePayer);
 * for (const ix of summary.instructions) {
 *   console.log(`${ix.programKind}: ${ix.stake?.name ?? ix.system?.name}`);
 * }
 * ```
 */
export function summarizeUnsignedTransaction(b64OrHex) {
    const tx = decodeUnsignedTransaction(b64OrHex);
    return summarizeTransaction(tx);
}
/**
 * Creates an assertion builder for custom validation logic.
 *
 * The builder provides a chainable API for validating transaction properties.
 * Call `.run()` at the end to throw on validation failures.
 *
 * @param b64OrHex - The unsigned transaction as base64 (preferred) or hex string
 * @returns A chainable assertion builder
 *
 * @example
 * ```ts
 * const builder = solana.buildAssertionBuilderFromUnsigned(base64Tx);
 * builder
 *   .requireStakeDelegate()
 *   .forbidStakeAuthorize()
 *   .instructionCountBetween(1, 5)
 *   .run(); // throws if any assertion fails
 * ```
 */
export function buildAssertionBuilderFromUnsigned(b64OrHex) {
    const tx = decodeUnsignedTransaction(b64OrHex);
    return TxAssertionBuilder.from(tx);
}
/**
 * Validates an unsigned Solana transaction against a set of rules.
 *
 * Returns a result object with validation status and any errors.
 * Does not throw on validation failure.
 *
 * @param b64OrHex - The unsigned transaction as base64 (preferred) or hex string
 * @param rules - Validation rules to apply
 * @returns Validation result with ok status, errors array, and transaction summary
 *
 * @example
 * ```ts
 * const { ok, errors, summary } = solana.validateUnsignedTransaction(base64Tx, {
 *   requireDelegate: true,
 *   forbidAuthorize: true,
 *   instructionCount: { min: 1, max: 5 },
 * });
 *
 * if (!ok) {
 *   console.error("Validation failed:", errors);
 * }
 * ```
 */
export function validateUnsignedTransaction(b64OrHex, rules = {}) {
    const tx = decodeUnsignedTransaction(b64OrHex);
    const builder = TxAssertionBuilder.from(tx);
    if (rules.requireAuthorize)
        builder.requireStakeAuthorize();
    if (rules.forbidAuthorize)
        builder.forbidStakeAuthorize();
    if (rules.requireDelegate)
        builder.requireStakeDelegate();
    if (rules.forbidDelegate)
        builder.forbidStakeDelegate();
    if (rules.feePayer)
        builder.feePayerIs(rules.feePayer);
    if (rules.instructionCount)
        builder.instructionCountBetween(rules.instructionCount.min, rules.instructionCount.max);
    if (rules.usesProgram)
        for (const p of rules.usesProgram)
            builder.usesProgram(p);
    if (rules.allowOnlyInstructions)
        builder.allowOnlyInstructions(rules.allowOnlyInstructions);
    if (rules.customize)
        rules.customize(builder, builder.getSummary());
    try {
        builder.run();
        return { ok: true, errors: [], summary: builder.getSummary() };
    }
    catch (e) {
        const maybeErrors = e?.errors;
        const msg = Array.isArray(maybeErrors)
            ? maybeErrors
            : [e instanceof Error ? e.message : String(e)];
        return {
            ok: false,
            errors: Array.isArray(msg) ? msg : [String(msg)],
            summary: builder.getSummary(),
        };
    }
}
//# sourceMappingURL=data:application/json;base64,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